import { useCallback, useEffect, useState } from "react";
import { useUser } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import { apiPost, getErrorMessage } from "../services/api";
import {
  createLeague,
  getUserLeagues,
  joinLeague,
  deleteLeague,
} from "../services/leagueService";
import LeagueCard from "../components/LeagueCard";
import AlertMessage from "../components/AlertMessage";
import PageHeader from "../components/PageHeader";
import SectionCard from "../components/SectionCard";
import EmptyState from "../components/EmptyState";

const ADMIN_USER_IDS = (process.env.REACT_APP_ADMIN_USER_IDS || "")
  .split(",")
  .map((s) => s.trim())
  .filter(Boolean);

function MyLeaguesPage({ onUserLoaded }) {
  const [leagues, setLeagues] = useState([]);
  const [name, setName] = useState("");
  const [tournamentType, setTournamentType] = useState("PremierLeague");
  const [inviteCode, setInviteCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");
  const [userId, setUserId] = useState(null);
  const [isCreatingLeague, setIsCreatingLeague] = useState(false);
  const [isJoiningLeague, setIsJoiningLeague] = useState(false);
  const [isDeletingLeague, setIsDeletingLeague] = useState(false);

  const { user, isLoaded } = useUser();
  const navigate = useNavigate();
  const isAdmin = user && ADMIN_USER_IDS.includes(user.id);

  const showError = useCallback((error, fallbackMessage) => {
    const resolvedMessage = getErrorMessage(error);
    setMessage(resolvedMessage || fallbackMessage);
    setMessageType("error");
  }, []);

  const clearMessage = useCallback(() => {
    setMessage("");
  }, []);

  const loadLeagues = useCallback(
    async (appUserId, signal) => {
      if (!appUserId) {
        setLeagues([]);
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const data = await getUserLeagues(appUserId, { signal });
        setLeagues(Array.isArray(data) ? data : []);
      } catch (error) {
        if (error?.name === "AbortError") return;
        console.error(error);
        showError(error, "Failed to load leagues.");
        setLeagues([]);
      } finally {
        setLoading(false);
      }
    },
    [showError]
  );

  useEffect(() => {
    if (!isLoaded) return;

    if (!user) {
      setUserId(null);
      setLeagues([]);
      setLoading(false);
      setMessage("You must be signed in to view your leagues.");
      setMessageType("error");
      return;
    }

    const controller = new AbortController();

    async function syncUser() {
      setLoading(true);

      try {
        const data = await apiPost(
          "/Users/sync",
          {
            clerkUserId: user.id,
            email: user.primaryEmailAddress?.emailAddress || "",
          },
          { signal: controller.signal }
        );

        const resolvedUserId =
          data?.id ?? data?.userId ?? data?.appUserId ?? null;

        if (!Number.isInteger(resolvedUserId) || resolvedUserId <= 0) {
          console.error("Unexpected /Users/sync response:", data);
          throw new Error("User sync returned invalid user ID.");
        }

        setUserId(resolvedUserId);

        if (onUserLoaded) {
          onUserLoaded(resolvedUserId);
        }
      } catch (error) {
        if (error?.name === "AbortError") return;
        console.error(error);
        setUserId(null);
        setLoading(false);
        showError(error, "Failed to load user.");
      }
    }

    syncUser();

    return () => controller.abort();
  }, [isLoaded, user, onUserLoaded, showError]);

  useEffect(() => {
    if (!userId) return;

    const controller = new AbortController();
    loadLeagues(userId, controller.signal);

    return () => controller.abort();
  }, [userId, loadLeagues]);

  async function handleCreateLeague() {
    if (isCreatingLeague || !name.trim() || !userId) return;

    // Check if World Cup and not admin
    if (tournamentType === "WorldCup" && !isAdmin) {
      showError(
        new Error("World Cup leagues are only available to admins."),
        "You don't have permission to create World Cup leagues."
      );
      return;
    }

    setIsCreatingLeague(true);
    clearMessage();

    const controller = new AbortController();

    try {
      await createLeague(name.trim(), userId, tournamentType);
      setName("");
      setTournamentType("PremierLeague");
      setMessage("League created.");
      setMessageType("success");
      await loadLeagues(userId, controller.signal);
    } catch (error) {
      console.error(error);
      showError(error, "Failed to create league.");
    } finally {
      setIsCreatingLeague(false);
    }
  }

  async function handleJoinLeague() {
    if (isJoiningLeague || !inviteCode.trim() || !userId) return;

    setIsJoiningLeague(true);
    clearMessage();

    const controller = new AbortController();

    try {
      await joinLeague(inviteCode.trim(), userId);
      setInviteCode("");
      setMessage("Joined league.");
      setMessageType("success");
      await loadLeagues(userId, controller.signal);
    } catch (error) {
      console.error(error);
      showError(error, "Failed to join league.");
    } finally {
      setIsJoiningLeague(false);
    }
  }

  async function handleDeleteLeague(leagueId) {
    if (!userId || !window.confirm("Are you sure you want to delete this league?")) {
      return;
    }

    setIsDeletingLeague(true);
    clearMessage();

    const controller = new AbortController();

    try {
      await deleteLeague(leagueId, userId, { signal: controller.signal });
      setMessage("League deleted successfully.");
      setMessageType("success");
      await loadLeagues(userId, controller.signal);
    } catch (error) {
      console.error(error);
      showError(error, "Failed to delete league.");
    } finally {
      setIsDeletingLeague(false);
    }
  }

  function handleOpenLeague(league) {
    if (!league?.id) {
      showError(
        new Error("This league could not be opened."),
        "Failed to open league."
      );
      return;
    }

    navigate(`/leagues/${league.id}`, { state: { league } });
  }

  if (!isLoaded || (loading && !userId && user)) {
    return (
      <div className="space-y-8">
        <div className="rounded-3xl bg-gradient-to-r from-slate-900 to-slate-800 p-6 sm:p-8">
          <div className="h-3 w-28 animate-pulse rounded bg-slate-700" />
          <div className="mt-3 h-8 w-48 animate-pulse rounded bg-slate-700" />
          <div className="mt-3 h-4 w-72 animate-pulse rounded bg-slate-700" />
        </div>
        <div className="grid gap-6 lg:grid-cols-2">
          {[1, 2].map((i) => (
            <SectionCard key={i}>
              <div className="space-y-4">
                <div className="h-11 animate-pulse rounded-2xl bg-slate-100" />
                <div className="h-11 animate-pulse rounded-2xl bg-slate-100" />
              </div>
            </SectionCard>
          ))}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="rounded-3xl bg-gradient-to-r from-slate-900 to-slate-800 p-6 sm:p-8">
          <div className="h-3 w-28 animate-pulse rounded bg-slate-700" />
          <div className="mt-3 h-8 w-48 animate-pulse rounded bg-slate-700" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="rounded-3xl bg-white p-4 sm:p-5 shadow-sm ring-1 ring-slate-200"
            >
              <div className="h-5 w-32 animate-pulse rounded bg-slate-200" />
              <div className="mt-2 h-4 w-20 animate-pulse rounded bg-slate-100" />
              <div className="mt-5 h-11 animate-pulse rounded-2xl bg-slate-100" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Dashboard"
        title="My Leagues"
        subtitle="Create a new league, join with an invite code, and open your current picks for this gameweek."
        dark
      />

      <AlertMessage message={message} type={messageType} />

      <section className="grid gap-6 lg:grid-cols-2">
        <SectionCard
          title="Create League"
          subtitle="Start a private league and invite friends with a code."
        >
          <div className="space-y-4">
            <input
              placeholder="League name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isCreatingLeague || isJoiningLeague || !userId}
              className="w-full rounded-2xl border border-slate-300 bg-white px-3 sm:px-4 py-2.5 sm:py-3 text-sm outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
            />

            <div className="rounded-2xl border border-slate-300 bg-white p-3 sm:p-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-700">
                Tournament Type
              </p>
              <div className="space-y-2">
                <label className="flex cursor-pointer items-center rounded-lg p-2 transition hover:bg-slate-50">
                  <input
                    type="radio"
                    name="tournament"
                    value="PremierLeague"
                    checked={tournamentType === "PremierLeague"}
                    onChange={(e) => setTournamentType(e.target.value)}
                    disabled={isCreatingLeague || isJoiningLeague || !userId}
                    className="h-4 w-4 cursor-pointer text-emerald-600"
                  />
                  <span className="ml-3 text-sm font-medium text-slate-700">
                    Premier League
                  </span>
                  <span className="ml-auto text-xs text-slate-500">
                    38 gameweeks
                  </span>
                </label>
                <label className={`flex cursor-pointer items-center rounded-lg p-2 transition ${!isAdmin ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-50"}`}>
                  <input
                    type="radio"
                    name="tournament"
                    value="WorldCup"
                    checked={tournamentType === "WorldCup"}
                    onChange={(e) => {
                      if (isAdmin) {
                        setTournamentType(e.target.value);
                      }
                    }}
                    disabled={isCreatingLeague || isJoiningLeague || !userId || !isAdmin}
                    className="h-4 w-4 cursor-pointer text-amber-600"
                  />
                  <span className="ml-3 text-sm font-medium text-slate-700">
                    World Cup
                  </span>
                  <span className="ml-auto text-xs text-slate-500">
                    {isAdmin ? "12 rounds" : "Admin only"}
                  </span>
                </label>
              </div>
            </div>

            <button
              onClick={handleCreateLeague}
              disabled={!name.trim() || !userId || isCreatingLeague || isJoiningLeague || (tournamentType === "WorldCup" && !isAdmin)}
              className="inline-flex w-full items-center justify-center rounded-2xl bg-emerald-600 px-3 sm:px-4 py-2.5 sm:py-3 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isCreatingLeague ? "Creating..." : "Create League"}
            </button>
          </div>
        </SectionCard>

        <SectionCard
          title="Join League"
          subtitle="Enter an invite code to join an existing league."
        >
          <div className="space-y-4">
            <input
              placeholder="Invite code"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value)}
              disabled={isCreatingLeague || isJoiningLeague || !userId}
              className="w-full rounded-2xl border border-slate-300 bg-white px-3 sm:px-4 py-2.5 sm:py-3 text-sm uppercase tracking-wider outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
            />

            <button
              onClick={handleJoinLeague}
              disabled={!inviteCode.trim() || !userId || isJoiningLeague || isCreatingLeague}
              className="inline-flex w-full items-center justify-center rounded-2xl bg-slate-900 px-3 sm:px-4 py-2.5 sm:py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isJoiningLeague ? "Joining..." : "Join League"}
            </button>
          </div>
        </SectionCard>
      </section>

      <section>
        <div className="mb-4">
          <h3 className="text-lg sm:text-xl font-semibold tracking-tight">Your Leagues</h3>
          <p className="mt-1 text-xs sm:text-sm text-slate-600">
            Open a league to start making your picks.
          </p>
        </div>

        {leagues.length === 0 ? (
          <EmptyState
            title="No leagues yet"
            description="Create one above or join with an invite code."
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {leagues.map((league) => (
              <LeagueCard
                key={league.id}
                league={league}
                onOpen={handleOpenLeague}
                onDelete={handleDeleteLeague}
                isAdmin={isAdmin}
                userId={userId}
                isDeleting={isDeletingLeague}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export default MyLeaguesPage;
